/**
 * Stats dashboard (presentational). Ports the legacy "stats-charts" subsystem
 * (docs/legacy/AVOID_LEGACY_ANALYSIS.md §2.9): an attack-death **donut** of the
 * six attack families plus headline survival numbers. Unlike the legacy screen
 * — which kept a 1s RTDB auto-refresh `Timer` running (easy to leak in the
 * background) — this is a pure component: it draws whatever {@link PlayerStats}
 * the shell hands it and never polls.
 *
 * No chart library: the donut is hand-rolled SVG (`stroke-dasharray` arcs on
 * concentric `<circle>`s) and the legend is a flexbox column — both a few dozen
 * bytes, no per-frame state.
 *
 * ARCHITECTURE: presentational only — holds no app state, talks to no service,
 * imports nothing from `game`/`services`/Firebase. The shell (App.tsx /
 * NetworkShell, owned elsewhere) fetches the numbers and passes them in. The
 * death-distribution is keyed by the canonical {@link AttackFamily} from
 * `core/types`, so the labels and colours stay in lock-step with the
 * simulation's death-attribution.
 *
 * Note on wording: the persisted field is named `totalDodged` (stable prop
 * contract — the shell wires against it), but every *user-facing* label reads
 * "avoided", matching the Barrage rename.
 */
import type { AttackFamily } from '../core/types';
import { useT } from './i18n';

/**
 * A death count per attack family. Every family is present (zero when the player
 * has never died to it) so the donut + legend render a stable, complete set of
 * six wedges. Mirrors the legacy `ATT_001..006` session counters.
 */
export type DeathsByFamily = Record<AttackFamily, number>;

/**
 * The headline survival numbers + the death distribution the screen renders.
 * Presentational shape only: the shell derives this from the backend (global
 * `STATS` + the user's validated runs) — this component never computes it.
 */
export interface PlayerStats {
  /** Total runs played (sum of all deaths, plus any in-progress run excluded). */
  runs: number;
  /** Best survival score (the leaderboard-relevant number). */
  bestScore: number;
  /** Mean survival score across all runs. */
  averageScore: number;
  /** Longest single survival time (ms) — drawn as a friendly mm:ss.S. */
  longestSurvivalMs: number;
  /** Lifetime hazards avoided (legacy `GAME_SESSION_END_INFO_005`, summed). */
  totalDodged: number;
  /** How each attack family has killed the player, keyed by {@link AttackFamily}. */
  deaths: DeathsByFamily;
}

/**
 * The six families in legacy display order, each with a human label and the
 * legacy donut hex colour (analysis §2.9 — `001=0x34495e … 006=0x8e44ad`). The
 * `AttackFamily` union order is walls/lasers/turret/caro/missiles/bombs, which we
 * mirror here so the legend matches the simulation's attribution buckets.
 */
const FAMILY_META: ReadonlyArray<{ key: AttackFamily; label: string; color: string }> = [
  { key: 'walls', label: 'Walls', color: '#34495e' },
  { key: 'lasers', label: 'Lasers', color: '#d35400' },
  { key: 'turret', label: 'Turret', color: '#c0392b' },
  { key: 'caro', label: 'Caro', color: '#e74c3c' },
  { key: 'missiles', label: 'Missiles', color: '#16a085' },
  { key: 'bombs', label: 'Bombs', color: '#8e44ad' },
];

/** SVG donut geometry. The circumference is the dasharray budget we split. */
const DONUT = { size: 200, stroke: 28, gap: 0.6 } as const;

interface StatsScreenProps {
  stats: PlayerStats;
  /** Back to the previous screen (menu / network shell). */
  onBack: () => void;
  /**
   * When true the screen is rendered INSIDE the POPSTAR tab shell, which owns the
   * single header/nav — so we suppress this screen's own back-header (and let it
   * sit flush under the shell's sub-nav). Standalone (false) it shows its header.
   */
  embedded?: boolean;
}

/** mm:ss.S from milliseconds (e.g. 92_400 → "1:32.4"). */
function formatDuration(ms: number): string {
  const totalSeconds = ms / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const tenths = Math.floor((totalSeconds * 10) % 10);
  return `${minutes}:${seconds.toString().padStart(2, '0')}.${tenths}`;
}

export function StatsScreen({ stats, onBack, embedded = false }: StatsScreenProps) {
  const t = useT();
  const totalDeaths = FAMILY_META.reduce((sum, f) => sum + (stats.deaths[f.key] ?? 0), 0);

  // Donut maths: radius is inset by half the stroke so the ring sits inside the
  // viewBox; we walk the circumference handing each family an arc proportional
  // to its share, offsetting the start so wedges sit end-to-end. A tiny `gap`
  // (degrees) is shaved off each visible wedge so neighbours read as distinct.
  const radius = (DONUT.size - DONUT.stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = DONUT.size / 2;
  const gapLen = (DONUT.gap / 360) * circumference;
  // How many families actually have deaths — used to decide whether a gap fits.
  const present = FAMILY_META.filter((f) => (stats.deaths[f.key] ?? 0) > 0).length;

  let arcOffset = 0;
  const wedges = FAMILY_META.map((f) => {
    const value = stats.deaths[f.key] ?? 0;
    const fraction = totalDeaths > 0 ? value / totalDeaths : 0;
    const full = fraction * circumference;
    // Only carve a gap when there's more than one wedge AND the wedge is large
    // enough to keep a visible sliver after trimming.
    const dash = present > 1 && full > gapLen * 1.5 ? full - gapLen : full;
    const wedge = {
      key: f.key,
      color: f.color,
      // dasharray: [visible arc, rest of ring]; rotate via dashoffset.
      dasharray: `${dash} ${circumference - dash}`,
      dashoffset: -arcOffset,
      value,
      fraction,
    };
    arcOffset += full;
    return wedge;
  });

  return (
    <div className={'screen stats-screen' + (embedded ? ' is-embedded' : '')}>
      <style>{STATS_CSS}</style>

      {!embedded && (
        <header className="network-head stats-head">
          <button className="btn btn-ghost network-back" onClick={onBack}>
            ‹ Back
          </button>
          <h1 className="network-title">STATS</h1>
        </header>
      )}

      <p className="stats-tagline">{t('stats.tagline')}</p>

      {/* ---- Headline survival numbers ---------------------------------- */}
      <section className="panel stats-headline">
        <h2>{t('stats.survival')}</h2>
        <div className="headline-grid">
          <div className="headline-cell headline-hero">
            <span className="headline-value">{stats.bestScore.toLocaleString()}</span>
            <span className="headline-label">{t('stats.bestScore')}</span>
          </div>
          <div className="headline-cell">
            <span className="headline-value">{stats.runs.toLocaleString()}</span>
            <span className="headline-label">{t('stats.runs')}</span>
          </div>
          <div className="headline-cell">
            <span className="headline-value">
              {Math.round(stats.averageScore).toLocaleString()}
            </span>
            <span className="headline-label">{t('stats.avgScore')}</span>
          </div>
          <div className="headline-cell">
            <span className="headline-value">{formatDuration(stats.longestSurvivalMs)}</span>
            <span className="headline-label">{t('stats.longestRun')}</span>
          </div>
          <div className="headline-cell">
            <span className="headline-value">{stats.totalDodged.toLocaleString()}</span>
            <span className="headline-label">{t('stats.totalAvoided')}</span>
          </div>
        </div>
      </section>

      {/* ---- Attack-death distribution donut ---------------------------- */}
      <section className="panel stats-deaths">
        <h2>{t('stats.deathsByAttack')}</h2>

        {totalDeaths === 0 ? (
          <p className="leaderboard-empty">{t('stats.noDeaths')}</p>
        ) : (
          <div className="donut-block">
            <div className="donut-wrap">
              <svg
                className="donut"
                viewBox={`0 0 ${DONUT.size} ${DONUT.size}`}
                role="img"
                aria-label={`Donut chart of ${totalDeaths} deaths by attack family`}
              >
                {/* track */}
                <circle
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="none"
                  stroke="var(--panel-2)"
                  strokeWidth={DONUT.stroke}
                />
                {/* wedges — rotated -90° so the first wedge starts at 12 o'clock */}
                <g transform={`rotate(-90 ${center} ${center})`}>
                  {wedges.map((w) =>
                    w.value > 0 ? (
                      <circle
                        key={w.key}
                        cx={center}
                        cy={center}
                        r={radius}
                        fill="none"
                        stroke={w.color}
                        strokeWidth={DONUT.stroke}
                        strokeLinecap="butt"
                        strokeDasharray={w.dasharray}
                        strokeDashoffset={w.dashoffset}
                      >
                        <title>{`${labelFor(w.key)}: ${w.value.toLocaleString()} (${Math.round(
                          w.fraction * 100,
                        )}%)`}</title>
                      </circle>
                    ) : null,
                  )}
                </g>
                {/* center total */}
                <text x={center} y={center - 6} textAnchor="middle" className="donut-total">
                  {totalDeaths.toLocaleString()}
                </text>
                <text x={center} y={center + 17} textAnchor="middle" className="donut-sub">
                  {t('stats.deaths')}
                </text>
              </svg>
            </div>

            <ul className="legend">
              {FAMILY_META.map((f) => {
                const value = stats.deaths[f.key] ?? 0;
                const pct = totalDeaths > 0 ? Math.round((value / totalDeaths) * 100) : 0;
                return (
                  <li key={f.key} className="legend-row" data-zero={value === 0 || undefined}>
                    <span className="swatch" style={{ background: f.color }} aria-hidden="true" />
                    <span className="legend-label">{t('family.' + f.key, f.label)}</span>
                    <span className="legend-bar" aria-hidden="true">
                      <span
                        className="legend-bar-fill"
                        style={{ width: `${pct}%`, background: f.color }}
                      />
                    </span>
                    <span className="legend-value">
                      {value.toLocaleString()} <span className="legend-pct">{pct}%</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}

/** Human label for an attack family (used in SVG `<title>` tooltips). */
function labelFor(key: AttackFamily): string {
  return FAMILY_META.find((f) => f.key === key)?.label ?? key;
}

/**
 * Screen-scoped styles. Kept co-located (rather than in src/index.css, owned by
 * another agent) so the component is self-contained; everything is nested under
 * `.stats-screen` so it can't leak. Shared primitives (`.screen`, `.panel`,
 * `.btn`, `.network-head`/`.network-title`/`.network-back`, CSS vars) come from
 * index.css. This is an arcade-family screen, so it inherits the sleek teal/cyan
 * accent (no `.network-screen` purple re-map).
 */
const STATS_CSS = `
.stats-screen { gap: 16px; }
.stats-screen .stats-head { margin-top: 4px; }
.stats-screen .network-title { font-size: 30px; letter-spacing: 0.08em; }

.stats-screen .stats-tagline {
  width: min(440px, 92vw);
  margin: -6px 0 2px;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
}

.stats-screen .panel { width: min(440px, 92vw); }

/* ---- Headline survival numbers ---- */
.stats-screen .headline-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}
.stats-screen .headline-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 14px 16px;
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-sm);
}
.stats-screen .headline-hero {
  grid-column: 1 / -1;
  background: linear-gradient(135deg, var(--accent-tint), var(--panel-2));
  border-color: var(--glass-border-strong);
}
.stats-screen .headline-hero .headline-value {
  font-size: 38px;
  background: linear-gradient(96deg, var(--accent), var(--accent-soft));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.stats-screen .headline-value {
  font-size: 24px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--text);
  line-height: 1.1;
}
.stats-screen .headline-label {
  font-size: 11px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

/* ---- Donut + legend ---- */
.stats-screen .donut-block {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
  justify-content: center;
}
.stats-screen .donut-wrap {
  flex: 0 0 auto;
  filter: drop-shadow(0 8px 18px rgba(0, 0, 0, 0.4));
}
.stats-screen .donut { width: 188px; height: 188px; display: block; }
.stats-screen .donut circle { transition: stroke-dashoffset var(--t-slow) var(--ease); }
.stats-screen .donut-total {
  fill: var(--text);
  font-size: 34px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.stats-screen .donut-sub {
  fill: var(--muted);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.stats-screen .legend {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 180px;
  flex: 1 1 180px;
}
.stats-screen .legend-row {
  display: grid;
  grid-template-columns: 12px auto 1fr auto;
  align-items: center;
  gap: 10px;
  font-size: 14px;
}
.stats-screen .legend-row[data-zero] { opacity: 0.45; }
.stats-screen .swatch {
  width: 12px;
  height: 12px;
  border-radius: 4px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
}
.stats-screen .legend-label { color: var(--text-dim); }
.stats-screen .legend-bar {
  height: 6px;
  border-radius: var(--radius-pill);
  background: var(--panel-2);
  overflow: hidden;
}
.stats-screen .legend-bar-fill {
  display: block;
  height: 100%;
  border-radius: var(--radius-pill);
  transition: width var(--t-slow) var(--ease);
}
.stats-screen .legend-value {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--text);
  white-space: nowrap;
}
.stats-screen .legend-pct { color: var(--muted); font-weight: 400; }

@media (max-width: 380px) {
  .stats-screen .headline-grid { grid-template-columns: 1fr; }
  .stats-screen .legend { min-width: 100%; flex-basis: 100%; }
}
`;
